import {createSlice, PayloadAction} from '@reduxjs/toolkit';

export type GlobalBannerVariant = 'info' | 'success' | 'warning' | 'error' | 'neutral';

export type GlobalBannerLink = {
  label: string;
  url: string;
  isExternal?: boolean;
};

export type GlobalBannerAction = {
  label: string;
  /** Action ID to look up the handler - use banner id as convention */
  actionId?: string;
  variant?: 'primary' | 'secondary' | 'outline';
};

export type GlobalBannerNotification = {
  id: string;
  text: string;
  variant?: GlobalBannerVariant;
  links?: GlobalBannerLink[];
  actions?: GlobalBannerAction[];
  dismissible?: boolean;
  /** Duration in seconds. Use null or undefined for infinite (won't auto-dismiss) */
  durationSeconds?: number | null;
};

export type GlobalBannerState = {
  notifications: GlobalBannerNotification[];
  bannerHeight: number;
};

// Estimated height per banner for immediate layout shift prevention
const ESTIMATED_BANNER_HEIGHT = 57;

const initialState: GlobalBannerState = {
  notifications: [],
  bannerHeight: 0,
};

export const GlobalBannerSlice = createSlice({
  name: 'globalBanner',
  initialState,
  reducers: {
    showBanner: (state, action: PayloadAction<GlobalBannerNotification>) => {
      // Avoid duplicates by ID
      const existingIndex = state.notifications.findIndex(n => n.id === action.payload.id);
      if (existingIndex === -1) {
        state.notifications.push(action.payload);
        // Immediately set estimated height to prevent layout shift
        state.bannerHeight = state.notifications.length * ESTIMATED_BANNER_HEIGHT;
      } else {
        // Update existing notification
        state.notifications[existingIndex] = action.payload;
      }
    },
    hideBanner: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter(n => n.id !== action.payload);
      // Update estimated height based on remaining banners
      state.bannerHeight = state.notifications.length * ESTIMATED_BANNER_HEIGHT;
    },
    hideAllBanners: (state) => {
      state.notifications = [];
      state.bannerHeight = 0;
    },
    setBannerHeight: (state, action: PayloadAction<number>) => {
      state.bannerHeight = action.payload;
    },
  },
});

export const GlobalBannerActions = GlobalBannerSlice.actions;

export default GlobalBannerSlice;
